import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../context/I18nContext';
import { categoriesService } from '../services/supabase';
import { Category } from '../types';
import { getTranslatedCategoryLabel } from '../utils/categoryTranslations';

export const useCategories = (type?: string) => {
  const { t, language } = useI18n();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = type
        ? await categoriesService.getByType(type)
        : await categoriesService.getAll();
      setCategories(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    fetchCategories();
  }, [type, fetchCategories]);

  const createCategory = async (category: Omit<Category, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const newCategory = await categoriesService.create(category);
      setCategories(prev => [...prev, newCategory]);
      return newCategory;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create category');
      throw err;
    }
  };

  const updateCategory = async (id: number, updates: Partial<Category>) => {
    try {
      const updatedCategory = await categoriesService.update(id, updates);
      setCategories(prev => prev.map(cat => cat.id === id ? updatedCategory : cat));
      return updatedCategory;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category');
      throw err;
    }
  };

  const deleteCategory = async (id: number) => {
    try {
      await categoriesService.delete(id);
      setCategories(prev => prev.filter(cat => cat.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
      throw err;
    }
  };

  /** Options for dropdowns: { value, label } using current language. */
  const getOptions = useCallback(() => {
    return categories.map(cat => ({
      value: cat.id.toString(),
      label: getTranslatedCategoryLabel(cat, t, language),
    }));
  }, [categories, t, language]);

  /** Options with an "All" first option (e.g. for filters). Pass the translated "All" label. */
  const getAllOptions = useCallback((allLabel: string) => {
    return [{ value: '', label: allLabel }, ...getOptions()];
  }, [getOptions]);

  /** Get translated label for a category id (for display). */
  const getLabel = useCallback((id: number | null | undefined): string => {
    if (id == null) return '';
    const cat = categories.find(c => c.id === id);
    return cat ? getTranslatedCategoryLabel(cat, t, language) : '';
  }, [categories, t, language]);

  /** Get category by id. */
  const getById = useCallback((id: number | null | undefined): Category | null => {
    if (id == null) return null;
    return categories.find(c => c.id === id) ?? null;
  }, [categories]);

  return {
    categories,
    loading,
    error,
    createCategory,
    updateCategory,
    deleteCategory,
    refresh: fetchCategories,
    getOptions,
    getAllOptions,
    getLabel,
    getById,
  };
};
